import React from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/localizedContent';

const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"];

function isSlotInPreviewRange(slot, startSlot, durationHours) {
  if (!startSlot || !durationHours || durationHours < 2) return false;
  const startIdx = TIME_SLOTS.indexOf(startSlot);
  const slotIdx = TIME_SLOTS.indexOf(slot);
  if (startIdx < 0 || slotIdx < 0) return false;
  return slotIdx >= startIdx && slotIdx < startIdx + durationHours;
}

export default function AvailabilityGrid({
  selectedDate,
  selectedSlot,
  availability,
  lang,
  onSelectSlot,
  durationHours = 1,
  previewReservedTableNumbers = [],
}) {
  const { t } = useLang();
  if (!selectedDate || !availability) return null;

  const tables = availability.tables || [];
  if (tables.length === 0) return null;

  const totalMaxCapacity = tables.reduce((sum, t) => sum + (t.capacity || 0), 0);
  const previewTables = Array.isArray(previewReservedTableNumbers) ? previewReservedTableNumbers : [];

  const slotCapacities = TIME_SLOTS.map(slot => {
    const available = tables.reduce((sum, t) => {
      const slotInfo = t.slots?.find(s => s.slot === slot);
      const apiFree = slotInfo ? slotInfo.available !== false : true;
      const previewBlock =
        previewTables.length > 0 &&
        previewTables.includes(t.table_number) &&
        isSlotInPreviewRange(slot, selectedSlot, durationHours);
      const isFree = apiFree && !previewBlock;
      return sum + (isFree ? (t.capacity || 0) : 0);
    }, 0);
    return { slot, available, pct: totalMaxCapacity > 0 ? available / totalMaxCapacity : 0 };
  });

  return (
    <div className="mb-6">
      <p className="text-[#A8B5AE] text-xs font-semibold uppercase tracking-wider mb-2">
        {t('reservationsShared.availability')} — {selectedDate}
        <span className="text-[#52525B] ml-2 normal-case">
          ({interpolate(t('reservationsShared.total_capacity'), { count: totalMaxCapacity })})
        </span>
        {onSelectSlot && (
          <span className="text-[#C9A962]/60 ml-2 normal-case font-normal">
            — {t('reservationsShared.click_to_select_time')}
          </span>
        )}
      </p>

      <div className="bg-[#0F1F1A] border border-[#C9A962]/10 rounded-xl p-3">
        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
          {slotCapacities.map(({ slot, available, pct }) => {
            const isSelected = selectedSlot === slot;
            const isFull = available === 0;
            const isPartial = available > 0 && available < totalMaxCapacity;
            const canSelect = !isFull && !!onSelectSlot;

            return (
              <div
                key={slot}
                onClick={() => canSelect && onSelectSlot(slot)}
                className={`rounded-lg p-2 text-center transition-all select-none ${
                  canSelect ? 'cursor-pointer' : ''
                } ${
                  isSelected
                    ? 'bg-[#C9A962]/30 border-2 border-[#C9A962]/70 shadow-lg'
                    : isFull
                    ? 'bg-[#EF4444]/10 border border-[#EF4444]/20 opacity-60'
                    : isPartial
                    ? `bg-[#F59E0B]/10 border border-[#F59E0B]/15 ${ canSelect ? 'hover:bg-[#F59E0B]/20 hover:border-[#F59E0B]/30' : ''}`
                    : `bg-[#2D8B70]/10 border border-[#2D8B70]/20 ${ canSelect ? 'hover:bg-[#2D8B70]/20 hover:border-[#2D8B70]/40 hover:scale-105' : ''}`
                }`}
              >
                <p className={`text-[10px] font-semibold ${
                  isSelected ? 'text-[#C9A962]' :
                  isFull ? 'text-[#EF4444]/70' :
                  isPartial ? 'text-[#F59E0B]/80' :
                  'text-[#2D8B70]/90'
                }`}>{slot}</p>

                <p className={`text-[9px] mt-0.5 ${
                  isSelected ? 'text-[#C9A962]/80' :
                  isFull ? 'text-[#EF4444]/50' :
                  isPartial ? 'text-[#F59E0B]/60' :
                  'text-[#2D8B70]/70'
                }`}>
                  {isFull
                    ? t('reservationsShared.full')
                    : `${available}p`}
                </p>

                <div className="h-1 rounded-full bg-white/5 mt-1 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isSelected ? 'bg-[#C9A962]' :
                      isFull ? 'bg-[#EF4444]/40' :
                      isPartial ? 'bg-[#F59E0B]/60' :
                      'bg-[#2D8B70]/60'
                    }`}
                    style={{ width: `${Math.round(pct * 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4 mt-3 pt-2 border-t border-white/5 flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-[#2D8B70]/40 border border-[#2D8B70]/30" />
            <span className="text-[#52525B] text-[9px]">{t('reservations.available')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-[#F59E0B]/30 border border-[#F59E0B]/20" />
            <span className="text-[#52525B] text-[9px]">{t('reservationsShared.partial')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-[#EF4444]/20 border border-[#EF4444]/15" />
            <span className="text-[#52525B] text-[9px]">{t('reservationsShared.complete')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-[#C9A962]/30 border-2 border-[#C9A962]/70" />
            <span className="text-[#52525B] text-[9px]">{t('reservationsShared.your_slot')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
